// Client-side preflight limits for a multi-video footage batch.
//
// Two limits, both of which the SERVER also enforces (storage.rules for the
// per-file size, the ingest trigger for the file count) — this is the UX
// guardrail that gives a good error before a multi-GB drop starts uploading.
//
// The pooled ACCOUNT storage allowance is a third ceiling and is evaluated by
// the shared photoUpload/caps.js `evaluateCaps` — footage bytes land in the
// same pool as photos and renders, so there is one pooled meter, not two.

// Per file. Mirrored in packages/functions/storage.rules
// (`videos/{uid}/{pid}/footage/{key}`), which is the enforcing copy — a client
// that bypasses this dialog is refused by the rule, not by us. 5 GB covers a
// full Brinno card chunk with headroom.
export const MAX_VIDEO_BYTES = 5 * 1024 * 1024 * 1024;

// Per project. Storage rules cannot count objects, so the enforcing copy lives
// in the ingest trigger (`FOOTAGE_FILE_CAP` in
// packages/functions/storage/background/main.py) and this is the friendly
// version of the same number. 100 files is ~3 months of Brinno daily rollover.
export const MAX_VIDEOS_PER_PROJECT = 100;

// Preflight estimate uses the same static 20 Mbps up assumption as the photo
// dialog; the live ETA replaces it once real throughput lands.
export { ASSUMED_UPLOAD_BPS, formatBytes, formatDuration } from '../photoUpload/caps';

export function evaluateVideoCaps({
  existingVideos = 0,
  batchVideos = 0,
  batchBytes = 0,
  // Entries in the batch, so oversize files can be named in the error.
  entries = [],
  maxVideoBytes = MAX_VIDEO_BYTES,
  maxVideosPerProject = MAX_VIDEOS_PER_PROJECT,
} = {}) {
  const projectedVideos = existingVideos + batchVideos;
  const oversizeEntries = (entries || []).filter(
    (e) => Number.isFinite(e?.sizeBytes) && e.sizeBytes > maxVideoBytes,
  );
  const overFileCount = projectedVideos > maxVideosPerProject;
  const hasOversize = oversizeEntries.length > 0;
  return {
    projectedVideos,
    batchBytes,
    maxVideoBytes,
    maxVideosPerProject,
    overFileCount,
    // How many of the batch would still fit — what the error message needs to
    // tell the user to remove.
    slotsRemaining: Math.max(0, maxVideosPerProject - existingVideos),
    oversizeEntries,
    hasOversize,
    blocked: overFileCount || hasOversize,
  };
}
